/**
 * Technician manual — EN + PT.
 *
 * Focused on the field-facing modules techs actually use on their phones:
 * Service Calls, Schedule, Time Clock, Daily Reports, Material Requests, Map.
 */

export const technicianManual = {
  en: {
    getting_started: {
      title: "Getting Started",
      path: "/",
      overview: "Welcome! This manual covers everything a JMO technician needs to know to use the system in the field. The app is mobile-first — you'll use it from your phone 90% of the time.",
      features: [
        "Login with the email and password given to you by the office.",
        "First-time login asks you to set a new password.",
        "Language switcher at the bottom of the sidebar (English / Portuguese).",
        "Bell icon (top right) shows notifications.",
      ],
      tasks: [
        { title: "Log in for the first time", steps: [
          "Open the login page in your phone's browser.",
          "Enter the email + temporary password from HR.",
          "The 'Must change password' modal opens — pick a new one, min 8 chars.",
          "You're in — the sidebar (☰) shows Service Calls, Schedule, Time Clock, Map, Material Requests.",
        ]},
        { title: "Bookmark the site as a home-screen app", steps: [
          "Open the site in Safari (iPhone) or Chrome (Android).",
          "Tap Share → 'Add to Home Screen'.",
          "The icon behaves like a native app.",
        ]},
      ],
      tips: [
        "If the app is slow, refresh the browser tab — the server is fine, the tab is stale.",
        "Never share your password. If you forget it, ask HR for a reset.",
      ],
    },

    tickets: {
      title: "Service Calls",
      path: "/tickets",
      overview: "Every job you go to is a Service Call. When the office assigns you a ticket, it shows up here.",
      features: [
        "List of your assigned tickets, sorted by date.",
        "Tap a ticket to open its detail view (mobile-optimized).",
        "Address, tenant contact, work steps, priority.",
        "Play / Stop buttons — start and end your timeclock for that job.",
        "Camera button for before/after photos.",
        "'Add purchase note' button — snap a receipt in the field.",
        "'Field records' card at the bottom: Material Requests + Daily Reports.",
      ],
      tasks: [
        { title: "Start work on a ticket", steps: [
          "Open the ticket.",
          "Tap the big Play button — your Time Clock starts, status becomes 'in progress'.",
          "Follow the service steps (checklist).",
        ]},
        { title: "Take photos", steps: [
          "Tap the Camera icon.",
          "Choose to take a new photo (Camera) or pick from your phone library (Library).",
          "Photos are compressed automatically before upload so they don't eat your data plan.",
          "Photos are visible to the office in real time.",
        ]},
        { title: "Attach a receipt from a hardware store", steps: [
          "'Add purchase note' → snap the receipt.",
          "AI reads amount + supplier — check the fields.",
          "Save — the office now sees a Material Request they'll pay for.",
        ]},
        { title: "Finish the job", steps: [
          "Complete all service steps.",
          "Take 'after' photos.",
          "Write a Daily Report (see next section).",
          "Tap the Stop button — clock stops, status becomes 'done'.",
        ]},
      ],
      tips: [
        "Photos are your protection. When a client claims you didn't do something, photos win.",
        "If a ticket has no address, the field is auto-filled from the property — no need to ask the office.",
      ],
      troubleshooting: [
        "'I can't clock in': the office may have marked the ticket 'blocked'. Contact your dispatcher.",
        "'The camera opens the wrong way': tap 'Library' to pick a photo you already took.",
      ],
    },

    schedule: {
      title: "Schedule",
      path: "/schedule",
      overview: "The Schedule shows what you have today, this week, and next week. Use it to plan your day.",
      features: [
        "Weekly grid with all your jobs.",
        "Card shows address (street), unit label if any, time window, and priority color.",
        "Tap a card to open the Service Call.",
      ],
      tasks: [
        { title: "Plan your morning", steps: [
          "Open Schedule → today's column.",
          "Note the addresses — cluster them geographically if possible.",
          "If two urgent tickets clash, call dispatch.",
        ]},
      ],
      tips: [
        "Blue = normal, amber = high, red = urgent. Take urgent tickets first.",
      ],
    },

    timeclock: {
      title: "Time Clock",
      path: "/timeclock",
      overview: "Records every minute you work. It's how your pay is calculated and how the office knows where you are.",
      features: [
        "Clock in from a Service Call (recommended).",
        "Manual clock-in / clock-out if you're not on a specific job.",
        "Break tracking.",
        "History of your entries.",
      ],
      tasks: [
        { title: "Clock in via a Service Call", steps: [
          "Open the ticket → tap Play. That's it — the office is notified automatically.",
        ]},
        { title: "Take a lunch break", steps: [
          "Tap the 'Break' button (or Stop temporarily) before leaving the site.",
          "Come back → tap 'Resume'.",
        ]},
      ],
      tips: [
        "Don't 'forget' to clock in — if you forget, you don't get paid for that hour.",
        "If you accidentally start the wrong ticket, stop it and start the correct one — the office can fix small mistakes.",
      ],
      troubleshooting: [
        "Clock-in fails: your phone may have lost signal. Try again once you have data.",
      ],
    },

    daily_reports: {
      title: "Daily Reports",
      path: "(inside your Service Call)",
      overview: "Daily Reports are what your client sees at the end of the day. It's your professional record of the work — photos, hours, materials, narrative. The app turns your notes into a polished PDF automatically.",
      features: [
        "Employee search — searches only teammates who track their time.",
        "Auto-populates YOUR hours for that day from Time Clock.",
        "Photos: Camera (fresh photo) or Library (photo already on your phone).",
        "Photos are compressed before upload so they don't take forever.",
        "AI narrative — writes a professional description from your raw notes.",
        "Sends the PDF to the client with one tap.",
      ],
      tasks: [
        { title: "Write a daily report", steps: [
          "Open the Service Call.",
          "Scroll down to 'Field records' → 'Daily Reports' → '+ Daily Report'.",
          "Type or dictate notes (voice-to-text works great).",
          "Add crew: search for your teammates by name — hours auto-fill from Time Clock.",
          "Add subcontractors if any showed up.",
          "Take photos (Camera button) or pick from Library.",
          "Optional: toggle 'AI polish' for a cleaner narrative.",
          "Save → the PDF is generated. You can email it to the client from the composer.",
        ]},
      ],
      tips: [
        "Do the report BEFORE you leave the site. Details fade fast.",
        "Voice-to-text (the microphone icon on your keyboard) saves a lot of typing.",
        "5-8 photos is the sweet spot — enough to tell the story, not so many the client is overwhelmed.",
      ],
      troubleshooting: [
        "'PDF didn't generate': your photo may be too big. Delete it and take a fresh one — the app compresses it before upload.",
        "'Can't find my teammate': they may not have any time entries yet. Ask them to clock in first, or type their name manually.",
      ],
    },

    material_requests: {
      title: "Material Requests",
      path: "/material-requests",
      overview: "When you need material and there isn't one on the truck, request it here. The office receives it immediately and Purchasing will handle it.",
      features: [
        "List your active requests with status (requested / approved / purchased / paid).",
        "Line items with qty and expected price.",
        "Auto-create from a Service Call's 'Request Material' button.",
      ],
      tasks: [
        { title: "Request material from the field", steps: [
          "Inside the Service Call → 'Field records' → 'Request Material' button.",
          "Add each item: description, qty, expected unit price.",
          "Submit — Purchasing is notified.",
        ]},
      ],
      tips: [
        "Include a description that actually identifies the part. '1' inch PVC elbow' is better than 'elbow'.",
        "Don't send duplicates — check your list first.",
      ],
    },

    map: {
      title: "Live Map",
      path: "/map",
      overview: "The map shows all today's active jobs on a map view. Handy for planning your driving route.",
      features: [
        "Pins for every scheduled or in-progress ticket.",
        "Your own location (if you granted GPS permission).",
      ],
      tips: [
        "Grant location permission in the browser — the office uses it to help dispatch.",
      ],
    },
  },

  pt: {
    getting_started: {
      title: "Começando",
      path: "/",
      overview: "Bem-vindo! Este manual cobre tudo que um técnico JMO precisa saber pra usar o sistema em campo. O app é mobile-first — você vai usar do celular 90% do tempo.",
      features: [
        "Login com o email e senha fornecidos pelo escritório.",
        "Primeira entrada pede pra trocar senha.",
        "Alternador de idioma no rodapé do menu lateral (Inglês / Português).",
        "Sininho (canto superior direito) mostra notificações.",
      ],
      tasks: [
        { title: "Fazer login pela primeira vez", steps: [
          "Abra a página de login no navegador do celular.",
          "Digite email + senha temporária do RH.",
          "O modal 'Must change password' abre — escolha nova senha, mínimo 8 chars.",
          "Está dentro — sidebar (☰) mostra Service Calls, Schedule, Time Clock, Map, Material Requests.",
        ]},
        { title: "Fixar como ícone na tela inicial", steps: [
          "Abra o site no Safari (iPhone) ou Chrome (Android).",
          "Compartilhar → 'Adicionar à Tela de Início'.",
          "O ícone abre como se fosse um app nativo.",
        ]},
      ],
      tips: [
        "Se o app ficar lento, atualize a aba do navegador — o servidor está bom, a aba está velha.",
        "Nunca compartilhe sua senha. Se esquecer, peça reset ao RH.",
      ],
    },

    tickets: {
      title: "Service Calls",
      path: "/tickets",
      overview: "Todo trabalho que você faz é um Service Call. Quando o escritório aloca um ticket pra você, aparece aqui.",
      features: [
        "Lista dos tickets alocados pra você, ordenados por data.",
        "Toque num ticket pra abrir os detalhes (otimizado pra mobile).",
        "Endereço, contato do inquilino, passos do serviço, prioridade.",
        "Botões Play / Stop — iniciam e terminam seu Time Clock naquele job.",
        "Botão Camera pra fotos antes/depois.",
        "'Add purchase note' — fotografa recibo em campo.",
        "Card 'Field records' no fim: Material Requests + Daily Reports.",
      ],
      tasks: [
        { title: "Começar a trabalhar num ticket", steps: [
          "Abra o ticket.",
          "Toque no Play grande — Time Clock começa, status vira 'in progress'.",
          "Siga os service steps (checklist).",
        ]},
        { title: "Tirar fotos", steps: [
          "Toque no ícone da câmera.",
          "Escolha tirar foto nova (Camera) ou pegar da biblioteca (Library).",
          "As fotos são comprimidas automaticamente antes do upload — não consome muito dado.",
          "O escritório vê as fotos em tempo real.",
        ]},
        { title: "Anexar recibo de material comprado", steps: [
          "'Add purchase note' → tire foto do recibo.",
          "AI lê o valor + fornecedor — confira os campos.",
          "Salve — o escritório agora vê uma Material Request pra pagar.",
        ]},
        { title: "Finalizar o job", steps: [
          "Complete todos os service steps.",
          "Tire fotos 'depois'.",
          "Escreva um Daily Report (próxima seção).",
          "Toque no Stop — clock para, status vira 'done'.",
        ]},
      ],
      tips: [
        "Fotos são sua proteção. Quando cliente reclamar, foto ganha.",
        "Se o ticket não tem endereço, o campo é auto-preenchido pela property — não precisa ligar pro escritório.",
      ],
      troubleshooting: [
        "'Não consigo bater ponto': o escritório pode ter marcado o ticket como 'blocked'. Ligue pro despachante.",
        "'Câmera abre errada': toque em 'Library' pra escolher uma foto que já tirou.",
      ],
    },

    schedule: {
      title: "Schedule",
      path: "/schedule",
      overview: "O Schedule mostra o que você tem hoje, esta semana e a semana que vem. Use pra planejar seu dia.",
      features: [
        "Grade semanal com todos seus jobs.",
        "Card mostra endereço (rua), unit label se houver, janela de horário e cor da prioridade.",
        "Toque num card pra abrir o Service Call.",
      ],
      tasks: [
        { title: "Planejar sua manhã", steps: [
          "Abra Schedule → coluna do dia.",
          "Anote os endereços — agrupe geograficamente se possível.",
          "Se dois urgentes se chocarem, ligue pro despachante.",
        ]},
      ],
      tips: [
        "Azul = normal, âmbar = high, vermelho = urgent. Pegue os urgentes primeiro.",
      ],
    },

    timeclock: {
      title: "Time Clock",
      path: "/timeclock",
      overview: "Registra cada minuto que você trabalha. É como seu pagamento é calculado e como o escritório sabe onde você está.",
      features: [
        "Bata ponto direto do Service Call (recomendado).",
        "Clock-in / clock-out manual se não estiver num job específico.",
        "Rastreio de pausas.",
        "Histórico das suas entradas.",
      ],
      tasks: [
        { title: "Bater ponto via Service Call", steps: [
          "Abra o ticket → toque Play. É isso — o escritório é notificado automaticamente.",
        ]},
        { title: "Fazer pausa de almoço", steps: [
          "Toque em 'Break' (ou Stop temporário) antes de sair.",
          "Voltou → 'Resume'.",
        ]},
      ],
      tips: [
        "Não 'esqueça' de bater ponto — se esquecer, não recebe por aquela hora.",
        "Se abrir o ticket errado por engano, pare e abra o certo — o escritório corrige pequenos erros.",
      ],
      troubleshooting: [
        "Clock-in falha: seu celular pode ter perdido sinal. Tente de novo com dados.",
      ],
    },

    daily_reports: {
      title: "Daily Reports",
      path: "(dentro do seu Service Call)",
      overview: "Daily Reports são o que o cliente vê no fim do dia. É seu registro profissional do trabalho — fotos, horas, materiais, texto. O app transforma suas notas num PDF profissional automaticamente.",
      features: [
        "Busca de employees — encontra só colegas que batem ponto.",
        "SUAS horas do dia entram automaticamente do Time Clock.",
        "Fotos: Camera (foto nova) ou Library (foto que já está no celular).",
        "Fotos são comprimidas antes do upload pra não demorar.",
        "Narrativa AI — escreve uma descrição profissional a partir das suas notas cruas.",
        "Envia o PDF pro cliente com 1 toque.",
      ],
      tasks: [
        { title: "Escrever daily report", steps: [
          "Abra o Service Call.",
          "Desça até 'Field records' → 'Daily Reports' → '+ Daily Report'.",
          "Digite ou dite notas (voz-para-texto funciona bem).",
          "Adicione crew: busque colegas pelo nome — horas preenchem automático.",
          "Adicione subcontratados se apareceram.",
          "Fotos (botão Camera) ou pegue da Library.",
          "Opcional: ative 'AI polish' pra narrativa polida.",
          "Salve → PDF é gerado. Pode enviar por email direto no composer.",
        ]},
      ],
      tips: [
        "Faça o report ANTES de sair do site. Detalhes esvaem rápido.",
        "Voz-para-texto (microfone no teclado) economiza muita digitação.",
        "5-8 fotos é o ponto ideal — conta a história sem sobrecarregar o cliente.",
      ],
      troubleshooting: [
        "'PDF não gerou': foto pode estar muito grande. Delete e tire de novo — o app comprime.",
        "'Não acho o colega': pode ser que ele ainda não tenha batido ponto. Peça pra ele bater primeiro, ou digite o nome manualmente.",
      ],
    },

    material_requests: {
      title: "Material Requests",
      path: "/material-requests",
      overview: "Quando precisar de material e não tem no caminhão, peça aqui. Escritório recebe na hora e Compras cuida.",
      features: [
        "Lista das suas requests com status (requested / approved / purchased / paid).",
        "Itens com qty e preço esperado.",
        "Criação auto pelo botão 'Request Material' do Service Call.",
      ],
      tasks: [
        { title: "Pedir material do campo", steps: [
          "Dentro do Service Call → 'Field records' → botão 'Request Material'.",
          "Adicione cada item: descrição, qty, preço unitário esperado.",
          "Enviar — Compras é notificado.",
        ]},
      ],
      tips: [
        "Descrição que identifica a peça de verdade. 'Cotovelo PVC 1 polegada' é melhor que 'cotovelo'.",
        "Não mande duplicado — confira sua lista primeiro.",
      ],
    },

    map: {
      title: "Mapa Ao Vivo",
      path: "/map",
      overview: "O mapa mostra todos os jobs ativos do dia. Bom pra planejar a rota.",
      features: [
        "Pins pra cada ticket agendado ou em andamento.",
        "Sua própria localização (se deu permissão de GPS).",
      ],
      tips: [
        "Autorize localização no navegador — o escritório usa pra ajudar no despacho.",
      ],
    },
  },
};
